import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import type { EoiContext } from 'bundles/expression-of-interest/types';

type ShortAnswerResponse = {
  'org.coursera.survey.question.ShortAnswerResponse': { text: string };
};

type DropdownResponse = {
  'org.coursera.survey.question.DropdownResponse': { id: string; text: string };
};

export type SurveySectionAnswers = Record<string, ShortAnswerResponse | DropdownResponse>;

export type CreateOrUpdateSurveySectionResponse = {
  QualificationSurveySectionResponse: {
    id: string;
    // The section to show next, not the one just submitted.
    sectionId: string;
    __typename: 'QualificationSurveySectionResponsesV2';
  };
};

export type CreateOrUpdateSurveySectionVariables = {
  productId: string;
  surveyVariant: string;
  shouldProcessTerminalActions: boolean;
  responseId: string;
  sectionId: string;
  eoiContext: EoiContext;
  responses: SurveySectionAnswers;
  trackingFields: Record<string, string>;
};

export const CREATE_OR_UPDATE_SURVEY_SECTION = gql`
  mutation CreateOrUpdateSurveySection(
    $productId: String!
    $surveyVariant: String!
    $shouldProcessTerminalActions: Boolean!
    $responseId: String
    $sectionId: String!
    $responses: DataMap!
    $eoiContext: DataMap!
    $trackingFields: DataMap
  ) {
    QualificationSurveySectionResponse(
      productId: $productId
      surveyVariant: $surveyVariant
      shouldProcessTerminalActions: $shouldProcessTerminalActions
      responseId: $responseId
      input: { sectionId: $sectionId, responses: $responses, eoiContext: $eoiContext, trackingFields: $trackingFields }
    )
      @rest(
        method: "POST"
        type: "QualificationSurveySectionResponsesV2"
        path: "qualificationSurveySectionResponses.v2?action=createOrUpdate&productId={args.productId}&surveyVariant={args.surveyVariant}&shouldProcessTerminalActions={args.shouldProcessTerminalActions}{args.responseId}"
      ) {
      id
      sectionId
    }
  }
`;

export type CreateOrUpdateSurveySectionInput = Omit<CreateOrUpdateSurveySectionVariables, 'responseId'> & {
  // Omit when submitting the first step
  responseId?: string;
};

// Empty for the first step, `&responseId=<id>` for later steps.
const formatResponseIdParam = (responseId?: string) =>
  responseId ? `&responseId=${encodeURIComponent(responseId)}` : '';

export const useCreateOrUpdateSurveySection = () => {
  const [mutate] = useMutation<CreateOrUpdateSurveySectionResponse, CreateOrUpdateSurveySectionVariables>(
    CREATE_OR_UPDATE_SURVEY_SECTION
  );

  const createOrUpdate = useCallback<
    (options: { variables: CreateOrUpdateSurveySectionInput }) => ReturnType<typeof mutate>
  >(
    ({ variables }) => mutate({ variables: { ...variables, responseId: formatResponseIdParam(variables.responseId) } }),
    [mutate]
  );

  return [createOrUpdate] as const;
};
